import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import { LIGHTMAP_AMBIENT } from './Lightmap3D';

export interface LightmapBindings {
  texture: THREE.Texture;
  mapSize: THREE.Vector2;
}

/** Bindings for the per-wall-tile red-light pulse effect. The red-mask
 *  texture shares the wall atlas's UV layout and stores only the red-light
 *  pixels; the pulse data texture is one texel per map tile, modulating the
 *  mask's contribution so each wall flashes on its own random schedule. */
export interface WallPulseBindings {
  redMask: THREE.Texture;
  pulse:   THREE.Texture;
  mapSize: THREE.Vector2;
}

/** Procedural surface detail for robot/weapon materials: etched panel lines,
 *  color-cycling trim, blinking LEDs, and a flowing conduit band. All parameters
 *  are scalars that can be tuned per-part to produce visually distinct surfaces
 *  from a single shader. UV space is triplanar-projected from the mesh's LOCAL
 *  position so the pattern sticks to the object as it moves through the world. */
export interface SkinOptions {
  /** Seed offset — gives each material a unique pattern even when they share geometry. */
  seed: number;
  /** Panel-line frequency (cycles per world unit). Higher = tighter panels. */
  panelScale?: number;
  /** How much panel-line etching darkens the base color (0..1). */
  etchStrength?: number;
  /** Fraction of small cells that contain an LED (0..1). */
  ledDensity?: number;
  /** Strength of the flowing conduit band (0..1). */
  conduitStrength?: number;
  /** Base hue for color-cycling trim + LEDs (0..1, wraps). */
  trimHue?: number;
}

export interface StylizedOptions {
  /** If provided, fragment color is multiplied by (ambient + sample(lightmap, worldXZ)). */
  lightmap?: LightmapBindings;
  /** Offset lightmap sample by the surface normal × halfTile. On for walls (so they
   *  pick up the adjacent floor tile's light instead of the wall/floor boundary);
   *  off for entities, which sit centered inside a single tile. */
  lightmapNormalOffset?: boolean;
  /** Half-Lambert contribution of the fake world-space "sun". 0 disables. */
  keyLightStrength?: number;
  /** Fresnel rim contribution (edge glow). 0 disables. */
  rimStrength?: number;
  /** Rim falloff exponent — higher = tighter band at silhouette edge. */
  rimPower?: number;
  /** Rim color. Default: cool cyan (contrasts warm ceiling lamps). */
  rimColor?: THREE.Color;
  /** Darkens fragments whose normal points downward — cheap stand-in for AO
   *  underneath enemies/robot. 0 disables. */
  verticalAOStrength?: number;
  /** Subtle colored tint on X-vs-Z-facing surfaces — makes N/S walls read differently
   *  from E/W walls. Used only for the map. */
  wallTint?: boolean;
  /** Procedural skin (panel lines + animated emissives) for robot/weapon parts. */
  skin?: SkinOptions;
  /** Per-wall-tile red-light pulse — only meaningful on wall/ceiling materials
   *  that use the wall atlas. */
  wallPulse?: WallPulseBindings;
}

/**
 * Shared time uniform in seconds. One `{value:0}` object bound into every
 * skinned material's shader so a single write propagates to all animated
 * emissives (LED blinks, trim hue cycle, conduit flow). Update this each
 * frame — e.g. `SKIN_TIME.value = performance.now() / 1000`.
 */
export const SKIN_TIME: { value: number } = { value: 0 };

// Shared stylized-sun direction. Upper-front-right relative to the iso camera.
// Module-level so all shaders agree on the light direction.
export const SUN_DIR   = new THREE.Vector3(0.4, 0.9, 0.35).normalize();
export const SUN_COLOR = new THREE.Color(1.0, 0.97, 0.88);

const RIM_COLOR_DEFAULT = new THREE.Color(0.40, 0.85, 1.00);
const WALL_TINT_X       = new THREE.Color(0.08, 0.05, 0.00); // warm bias on ±X walls
const WALL_TINT_Z       = new THREE.Color(0.00, 0.04, 0.08); // cool bias on ±Z walls

/**
 * Patches a MeshBasicMaterial so its fragment color is post-processed by a
 * stylized lighting stack: lightmap (optional), half-Lambert sun key,
 * fresnel rim, vertical AO, and wall-normal tinting. Each effect is gated
 * behind a shader-level `#define` so disabled features cost nothing at runtime.
 *
 * The injection runs just before `<tonemapping_fragment>`, so tone mapping and
 * sRGB encoding still apply to the final composited color.
 */
export function applyStylizedShading(
  mat: THREE.MeshBasicMaterial,
  opts: StylizedOptions = {},
): void {
  const {
    lightmap,
    lightmapNormalOffset = false,
    keyLightStrength = 0.25,
    rimStrength = 0.0,
    rimPower = 3.0,
    rimColor = RIM_COLOR_DEFAULT,
    verticalAOStrength = 0.0,
    wallTint = false,
    skin,
    wallPulse,
  } = opts;

  const defines: string[] = [];
  if (lightmap)                 defines.push('USE_SS_LIGHTMAP');
  if (lightmap && lightmapNormalOffset) defines.push('USE_SS_LM_NORMAL_OFFSET');
  if (keyLightStrength > 0)     defines.push('USE_SS_KEY');
  if (rimStrength > 0)          defines.push('USE_SS_RIM');
  if (verticalAOStrength > 0)   defines.push('USE_SS_AO');
  if (wallTint)                 defines.push('USE_SS_WALL_TINT');
  if (skin)                     defines.push('USE_SS_SKIN');
  if (wallPulse)                defines.push('USE_SS_WALL_PULSE');

  const definesBlock = defines.map(d => `#define ${d}`).join('\n         ');

  mat.onBeforeCompile = (shader) => {
    if (lightmap) {
      shader.uniforms.uSSLightmap = { value: lightmap.texture };
      shader.uniforms.uSSMapSize  = { value: lightmap.mapSize };
      shader.uniforms.uSSAmbient  = { value: LIGHTMAP_AMBIENT };
      shader.uniforms.uSSHalfTile = { value: TILE_SIZE * 0.5 };
    }
    if (keyLightStrength > 0) {
      shader.uniforms.uSSSunDir       = { value: SUN_DIR };
      shader.uniforms.uSSSunColor     = { value: SUN_COLOR };
      shader.uniforms.uSSKeyStrength  = { value: keyLightStrength };
    }
    if (rimStrength > 0) {
      shader.uniforms.uSSRimColor    = { value: rimColor };
      shader.uniforms.uSSRimPower    = { value: rimPower };
      shader.uniforms.uSSRimStrength = { value: rimStrength };
    }
    if (verticalAOStrength > 0) {
      shader.uniforms.uSSAOStrength = { value: verticalAOStrength };
    }
    if (wallTint) {
      shader.uniforms.uSSWallTintX = { value: WALL_TINT_X };
      shader.uniforms.uSSWallTintZ = { value: WALL_TINT_Z };
    }
    if (wallPulse) {
      shader.uniforms.uSSRedMask      = { value: wallPulse.redMask };
      shader.uniforms.uSSWallPulse    = { value: wallPulse.pulse };
      shader.uniforms.uSSWallPulseMap = { value: wallPulse.mapSize };
    }
    if (skin) {
      // Share the SKIN_TIME object across all skinned materials so one write
      // (from the robot rig's per-frame update) animates every part in sync.
      shader.uniforms.uSSTime           = SKIN_TIME;
      shader.uniforms.uSSSkinSeed       = { value: skin.seed };
      shader.uniforms.uSSSkinPanelScale = { value: skin.panelScale ?? 0.6 };
      shader.uniforms.uSSSkinEtch       = { value: skin.etchStrength ?? 0.35 };
      shader.uniforms.uSSSkinLedDensity = { value: skin.ledDensity ?? 0.08 };
      shader.uniforms.uSSSkinConduit    = { value: skin.conduitStrength ?? 0.0 };
      shader.uniforms.uSSSkinTrimHue    = { value: skin.trimHue ?? 0.55 };
    }

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
         ${definesBlock}
         varying vec3 vSSWorldPos;
         varying vec3 vSSWorldNormal;
         #ifdef USE_SS_SKIN
           varying vec3 vSSLocalPos;
           varying vec3 vSSLocalNormal;
         #endif
         #ifdef USE_SS_WALL_PULSE
           varying vec2 vSSUv;
         #endif`,
      )
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
         vec4 ssWP_ = modelMatrix * vec4(position, 1.0);
         vSSWorldPos    = ssWP_.xyz;
         vSSWorldNormal = normalize(mat3(modelMatrix) * normal);
         #ifdef USE_SS_SKIN
           vSSLocalPos    = position;
           vSSLocalNormal = normal;
         #endif
         #ifdef USE_SS_WALL_PULSE
           vSSUv = uv;
         #endif`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
         ${definesBlock}
         varying vec3 vSSWorldPos;
         varying vec3 vSSWorldNormal;
         #ifdef USE_SS_KEY
           uniform vec3  uSSSunDir;
           uniform vec3  uSSSunColor;
           uniform float uSSKeyStrength;
         #endif
         #ifdef USE_SS_LIGHTMAP
           uniform sampler2D uSSLightmap;
           uniform vec2  uSSMapSize;
           uniform vec3  uSSAmbient;
           uniform float uSSHalfTile;
         #endif
         #ifdef USE_SS_RIM
           uniform vec3  uSSRimColor;
           uniform float uSSRimPower;
           uniform float uSSRimStrength;
         #endif
         #ifdef USE_SS_AO
           uniform float uSSAOStrength;
         #endif
         #ifdef USE_SS_WALL_TINT
           uniform vec3 uSSWallTintX;
           uniform vec3 uSSWallTintZ;
         #endif
         #ifdef USE_SS_WALL_PULSE
           uniform sampler2D uSSRedMask;
           uniform sampler2D uSSWallPulse;
           uniform vec2  uSSWallPulseMap;
           varying vec2  vSSUv;
         #endif
         #ifdef USE_SS_SKIN
           varying vec3  vSSLocalPos;
           varying vec3  vSSLocalNormal;
           uniform float uSSTime;
           uniform float uSSSkinSeed;
           uniform float uSSSkinPanelScale;
           uniform float uSSSkinEtch;
           uniform float uSSSkinLedDensity;
           uniform float uSSSkinConduit;
           uniform float uSSSkinTrimHue;
           vec3 ssHsv(vec3 c) {
             vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
             vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
             return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
           }
           float ssH2(vec2 p) {
             return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
           }
           // Value noise — bilinear-interpolated 2D hash grid.
           float ssNoise(vec2 p) {
             vec2 i = floor(p);
             vec2 f = fract(p);
             float a = ssH2(i);
             float b = ssH2(i + vec2(1.0, 0.0));
             float c = ssH2(i + vec2(0.0, 1.0));
             float d = ssH2(i + vec2(1.0, 1.0));
             vec2 u = f * f * (3.0 - 2.0 * f);
             return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
           }
           // 3-octave FBM — broad stains at small scales, fine grit at large.
           float ssFbm(vec2 p) {
             float v = 0.0;
             float a = 0.5;
             for (int k = 0; k < 3; k++) {
               v += a * ssNoise(p);
               p *= 2.0;
               a *= 0.5;
             }
             return v;
           }
           // Triplanar UV using the LOCAL normal so the pattern stays stable
           // when the mesh rotates (world normal would flip dominant axis).
           vec2 ssTriUV(vec3 N, vec3 lp) {
             vec3 a = abs(N);
             if (a.y >= a.x && a.y >= a.z) return lp.xz;
             if (a.x >= a.z)               return lp.zy;
             return lp.xy;
           }
         #endif`,
      )
      .replace(
        '#include <tonemapping_fragment>',
        `{
           vec3 ssN = normalize(vSSWorldNormal);
           vec3 ssV = normalize(cameraPosition - vSSWorldPos);
           vec3 ssCol = gl_FragColor.rgb;

           #ifdef USE_SS_SKIN
             // Triplanar UV via LOCAL normal — stable under mesh rotation.
             vec3 ssLocalN = normalize(vSSLocalNormal);
             vec2 ssSkinUV = ssTriUV(ssLocalN, vSSLocalPos) * uSSSkinPanelScale
                             + vec2(uSSSkinSeed, uSSSkinSeed * 1.73);

             // ── Panel geometry (big structural seams + finer sub-panels) ──
             vec2  ssBigCell = floor(ssSkinUV * 0.5);
             float ssBigH    = ssH2(ssBigCell + uSSSkinSeed);
             vec2  ssBig     = abs(fract(ssSkinUV * 0.5) - 0.5) * 2.0;
             vec2  ssSml     = abs(fract(ssSkinUV * 2.0) - 0.5) * 2.0;
             // Jitter big-seam width per cell so panel joins don't look machined.
             float ssBigW    = 0.07 + ssBigH * 0.09;
             float ssBigLine = 1.0 - smoothstep(0.0, ssBigW, min(ssBig.x, ssBig.y));
             float ssSmlLine = 1.0 - smoothstep(0.0, 0.06, min(ssSml.x, ssSml.y));

             // ── Noise fields — broad grime + fine grit ───────────────────
             float ssGrunge = ssFbm(ssSkinUV * 1.35);
             float ssGrit   = ssFbm(ssSkinUV * 6.50);

             // ── Per-panel variation (brightness + warm/cool hue drift) ───
             float ssPanelShade = 0.78 + ssBigH * 0.44;
             float ssPanelHue   = ssBigH * 2.0 - 1.0;
             vec3  ssPanelTint  = vec3(1.0 + ssPanelHue * 0.06,
                                       1.0 - abs(ssPanelHue) * 0.03,
                                       1.0 - ssPanelHue * 0.06);
             ssCol *= ssPanelShade * ssPanelTint;

             // ── Fine grit (value noise × base) ───────────────────────────
             ssCol *= 0.80 + ssGrit * 0.36;

             // ── Soft grime blotches (dark patches where dirt collects) ──
             float ssGrime = smoothstep(0.58, 0.28, ssGrunge);
             ssCol *= 1.0 - ssGrime * 0.24;

             // ── Panel etch, deepened where grime accumulates in seams ──
             ssCol *= 1.0 - ssBigLine * uSSSkinEtch * (0.55 + ssGrime * 0.70);
             ssCol *= 1.0 - ssSmlLine * uSSSkinEtch * 0.25;

             // ── Anisotropic scratches (stretched noise along V axis) ───
             vec2  ssScrUV   = vec2(ssSkinUV.x * 0.45 + ssSkinUV.y * 0.12,
                                    ssSkinUV.y * 8.5  + ssSkinUV.x * 0.35);
             float ssScratch = smoothstep(0.78, 0.92, ssNoise(ssScrUV));
             ssCol *= 1.0 - ssScratch * 0.26;

             // ── Rivets at big-panel inner corners (select cells only) ──
             if (ssBigH > 0.42) {
               vec2  ssBigF   = fract(ssSkinUV * 0.5);
               vec2  ssCornD  = min(ssBigF, 1.0 - ssBigF);
               float ssRivet  = 1.0 - smoothstep(0.018, 0.042, length(ssCornD - vec2(0.065)));
               ssCol *= 1.0 - ssRivet * 0.50;
             }
           #endif

           #ifdef USE_SS_LIGHTMAP
             #ifdef USE_SS_LM_NORMAL_OFFSET
               vec2 ssSmp = vSSWorldPos.xz + ssN.xz * uSSHalfTile;
             #else
               vec2 ssSmp = vSSWorldPos.xz;
             #endif
             vec3 ssLm  = texture2D(uSSLightmap, ssSmp / uSSMapSize).rgb;
             vec3 ssEnv = uSSAmbient + ssLm;
             ssCol *= ssEnv;
           #else
             vec3 ssEnv = vec3(1.0);
           #endif

           #ifdef USE_SS_KEY
             float ssNdL = dot(ssN, uSSSunDir);
             float ssHL  = ssNdL * 0.5 + 0.5;
             ssHL *= ssHL;
             ssCol += ssCol * uSSSunColor * (uSSKeyStrength * ssHL);
           #endif

           #ifdef USE_SS_RIM
             float ssNdV = max(0.0, dot(ssN, ssV));
             float ssFres = pow(1.0 - ssNdV, uSSRimPower);
             // Scale rim by local brightness so dark-room enemies don't get
             // bright cyan edges — they should still read as "in shadow".
             #ifdef USE_SS_LIGHTMAP
               float ssRimMod = 0.30 + 0.70 * clamp(max(max(ssEnv.r, ssEnv.g), ssEnv.b), 0.0, 1.5);
             #else
               float ssRimMod = 1.0;
             #endif
             ssCol += uSSRimColor * (ssFres * uSSRimStrength * ssRimMod);
           #endif

           #ifdef USE_SS_AO
             float ssDown = clamp(-ssN.y, 0.0, 1.0);
             ssCol *= (1.0 - ssDown * uSSAOStrength);
           #endif

           #ifdef USE_SS_WALL_TINT
             ssCol += ssCol * (uSSWallTintX * abs(ssN.x) + uSSWallTintZ * abs(ssN.z));
           #endif

           #ifdef USE_SS_WALL_PULSE
             // Mask carries the red-light pixels baked into each wall SVG.
             // Skip the per-tile lookup entirely when the mask is transparent
             // — most atlas pixels aren't lights, so this branch is taken on
             // the majority of fragments and saves a sample.
             vec4 ssMask = texture2D(uSSRedMask, vSSUv);
             if (ssMask.a > 0.01) {
               // Offset by -normal*halfTile so a wall face samples its OWN
               // tile's pulse value (not the adjacent floor tile's zero).
               // Ceiling faces have normal=+Y → XZ offset is zero, which
               // already lands on the wall tile centre. ClampToEdge keeps
               // border tiles from sampling off-map texels.
               vec2 ssPUV = (vSSWorldPos.xz - ssN.xz * uSSHalfTile) / uSSWallPulseMap;
               float ssPulse = texture2D(uSSWallPulse, ssPUV).r;
               ssCol += ssMask.rgb * ssPulse * 3.5;
             }
           #endif

           #ifdef USE_SS_SKIN
             // Emissive layer runs AFTER lighting so LEDs / conduit / trim
             // glow through shadow instead of being darkened by the lightmap.
             vec3 ssEmit = vec3(0.0);

             // Color-cycling trim — only a subset of big-panel cells; each
             // cell picks a phase offset so neighbours don't all cycle together.
             // (ssBigCell / ssBigH are already defined in the etch block above.)
             if (ssBigH > 0.55) {
               float ssTrimPhase = uSSSkinTrimHue + uSSTime * 0.10 + ssBigH * 2.0;
               vec3  ssTrimCol   = ssHsv(vec3(fract(ssTrimPhase), 0.85, 1.0));
               ssEmit += ssTrimCol * ssBigLine * ((ssBigH - 0.55) * 2.2) * 0.65;
             }

             // Blinking LEDs — fine grid, sparse cells hold dots that pulse
             // with varied hue + frequency per cell.
             vec2  ssLedCell = floor(ssSkinUV * 3.0);
             float ssLedH    = ssH2(ssLedCell + vec2(uSSSkinSeed * 2.7, uSSSkinSeed * 4.1));
             if (ssLedH > 1.0 - uSSSkinLedDensity) {
               vec2  ssLedLP = fract(ssSkinUV * 3.0) - 0.5;
               float ssR    = length(ssLedLP);
               float ssDot  = smoothstep(0.16, 0.04, ssR);
               float ssPh   = ssLedH * 31.7 + uSSTime * (0.8 + ssLedH * 4.0);
               float ssBlnk = pow(max(0.0, sin(ssPh)), 3.0);
               vec3  ssLedC = ssHsv(vec3(fract(ssLedH * 13.0 + uSSSkinTrimHue + 0.35), 0.95, 1.0));
               ssEmit += ssLedC * ssDot * ssBlnk * 2.4;
             }

             // Flowing conduit band — sweeps along the U axis with hue cycling.
             float ssBand    = abs(fract(ssSkinUV.x * 0.18 - uSSTime * 0.35) - 0.5);
             float ssConduit = smoothstep(0.06, 0.0, ssBand);
             vec3  ssConCol  = ssHsv(vec3(fract(uSSTime * 0.07 + uSSSkinTrimHue + 0.5), 0.85, 1.0));
             ssEmit += ssConCol * ssConduit * uSSSkinConduit * 1.1;

             ssCol += ssEmit;
           #endif

           gl_FragColor.rgb = ssCol;
         }
         #include <tonemapping_fragment>`,
      );
  };
  mat.needsUpdate = true;
}
